import api from "./api";
import { toast } from "sonner";

const RAZORPAY_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

export function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = RAZORPAY_SCRIPT;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export async function openRazorpayCheckout({ planId, user, onSuccess, onFailure, onDismiss }) {
  const ok = await loadRazorpayScript();
  if (!ok || !window.Razorpay) {
    toast.error("Could not load Razorpay. Check your connection.");
    onDismiss?.();
    return;
  }
  let order;
  try {
    const { data } = await api.post("/payments/checkout", { plan_id: planId });
    order = data;
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Could not start checkout");
    onDismiss?.();
    return;
  }
  const options = {
    key: order.key_id,
    amount: order.amount,
    currency: order.currency,
    order_id: order.order_id,
    name: "SkillGap Pilot",
    description: `${order.plan_name} plan`,
    theme: { color: "#6366f1" },
    prefill: {
      name: user?.full_name || user?.name || "",
      email: user?.email || "",
      contact: user?.mobile || user?.phone || "",
      // Make UPI the default selected method when the checkout opens
      method: "upi",
    },
    notes: { plan_id: order.plan_id },
    // Explicitly enable UPI and other major methods. Do NOT use config.display.blocks
    // with a single "upi" block — that suppresses the default UPI rail rendering on
    // some merchant accounts. Let Razorpay render its default UPI block alongside.
    method: { upi: true, card: true, netbanking: true, wallet: true, paylater: true, emi: false },
    handler: async (response) => {
      try {
        const v = await api.post("/payments/verify", {
          razorpay_order_id: response.razorpay_order_id,
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_signature: response.razorpay_signature,
        });
        if (v.data?.idempotent) toast.info("Payment already processed");
        else toast.success("Payment verified — subscription activated!");
        onSuccess?.(response, v.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Payment verification failed");
        onFailure?.(response, e);
      }
    },
    modal: { ondismiss: () => { toast.info("Payment cancelled"); onDismiss?.(); } },
  };
  try {
    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", (resp) => {
      toast.error(resp?.error?.description || "Payment failed");
      onFailure?.(resp, null);
    });
    rzp.open();
  } catch (e) {
    toast.error("Unable to open Razorpay checkout");
    onDismiss?.();
  }
}
